<template>
  <!-- 其它规则 -->
  <div style="margin-top: 10px;">
    <el-form
      ref="ruleForm"
      :model="formData"
      label-width="0"
      class="error-form"
      size="mini"
    >
      <el-table :data="formData.arr" border>
        <!-- 费用类型 -->
        <el-table-column min-width="120">
          <template slot="header">
            <div>
              <span style="color: red;">*</span>
              <span>{{ $t('basicData.ExpenseType') }}</span>
            </div>
          </template>
          <template slot-scope="scope">
            <el-form-item
              :prop="'arr.' + scope.$index + '.feeTypeCode'"
              :rules="feeTypeCodeRules"
            >
              <el-select
                v-model="scope.row.feeTypeCode"
                :placeholder="$t('basicData.PleaseSelect')"
                style="width: 100%;"
                @change="handleChange($event, scope.$index)"
              >
                <el-option
                  v-for="item in enabledFeeList"
                  :key="item.id"
                  :label="getFeeTypeLabel(item)"
                  :value="item.code"
                  :disabled="isFeeTypeDisabled(item.code, scope.row.feeTypeCode, scope.$index)"
                />
              </el-select>
            </el-form-item>
          </template>
        </el-table-column>

        <!-- 计费规则 -->
        <el-table-column min-width="300">
          <template slot="header">
            <div>
              <span style="color: red;">*</span>
              <span>{{ $t('basicData.BillingRules') }}(%)</span>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="feet-r">
              <div v-if="scope.row.specialRules === 2" class="feet-r">
                <el-form-item
                  :prop="`arr.${scope.$index}.startNumber`"
                  :rules="getRangeRules(scope.row.endNumber, 'start')"
                >
                  <el-input-number
                    v-model="scope.row.startNumber"
                    :min="0.1"
                    :max="MAX_NUMBER"
                    :precision="1"
                    :controls="false"
                    :placeholder="$t('basicData.PleaseEnter')"
                  />
                </el-form-item>
                <span> &lt; {{ $t('basicData.delayTimeCalculation') + ' ≤ ' }}</span>
                <el-form-item
                  :prop="`arr.${scope.$index}.endNumber`"
                  :rules="getRangeRules(scope.row.startNumber, 'end')"
                >
                  <el-input-number
                    v-model="scope.row.endNumber"
                    :min="0.2"
                    :max="MAX_NUMBER"
                    :precision="1"
                    :controls="false"
                    :placeholder="$t('basicData.PleaseEnter')"
                  />
                </el-form-item>
                <span>{{ $t('basicData.penaltyAmountFormula') + '*' }}</span>
              </div>

              <div class="flex">
                <el-form-item
                  :prop="'arr.' + scope.$index + '.deliveryFeeRatio'"
                  :rules="deliveryFeeRatioRules"
                >
                  <el-input
                    v-model="scope.row.deliveryFeeRatio"
                    :placeholder="$t('basicData.PleaseEnter')"
                    style="width: 120px"
                    @input="handleDeliveryFeeRatioInput($event, scope.$index)"
                  >
                    <template slot="append">%</template>
                  </el-input>
                </el-form-item>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column width="100">
          <template slot-scope="scope">
            <el-button
              size="small"
              type="danger"
              @click="handleDel(scope.$index)"
            >{{ $t('basicData.Delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash';

export default {
  name: 'BillingRulesTable',

  props: {
    tableData: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value)
    },
    feeList: {
      type: Array,
      default: () => [],
      required: true,
      validator: (value) => Array.isArray(value)
    }
  },

  data() {
    // 范围规则验证器
    const createRangeValidator = (type) => (rule, value, callback) => {
      const { required } = rule;
      const compareValue = rule.compareValue;

      // 处理空值
      if (value === null || value === '' || value === undefined) {
        if (required) {
          return callback(new Error(this.$t('basicData.CannotBeEmpty')));
        }
        return callback();
      }

      // 转换为数字
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return callback(new Error(this.$t('basicData.IncorrectFormatting')));
      }

      // 小数位数检查（只允许一位小数）
      const decimalPart = value.toString().split('.')[1];
      if (decimalPart && decimalPart.length > 1) {
        return callback(new Error(this.$t('basicData.OnlyOneDecimalAllowed')));
      }

      // 范围检查
      const min = type === 'start' ? 0.1 : 0.2;
      const max = this.MAX_NUMBER;

      if (numValue < min || numValue > max) {
        return callback(new Error(
          this.$t('basicData.MustBeBetween', { min, max })
        ));
      }

      // 区间逻辑检查
      if (compareValue !== null && compareValue !== undefined && compareValue !== '') {
        const compareNum = parseFloat(compareValue);
        if (type === 'start' && numValue >= compareNum) {
          return callback(new Error(this.$t('basicData.MustLessThanEndValue')));
        }
        if (type === 'end' && numValue <= compareNum) {
          return callback(new Error(this.$t('basicData.MustGreaterThanStartingValue')));
        }
      }

      callback();
    };

    return {
      formData: {
        arr: []
      },
      MAX_NUMBER: 10000,
      selectedFeeTypeCodes: new Set(), // 使用Set提高查找性能
      rangeValidator: createRangeValidator
    };
  },

  computed: {
    // 费用类型选择规则
    feeTypeCodeRules() {
      return [
        {
          required: true,
          message: this.$t('basicData.CannotBeEmpty'),
          trigger: ['blur', 'change']
        },
        {
          validator: this.validateFeeTypeUniqueness,
          trigger: ['blur', 'change']
        }
      ];
    },

    // 费用比例规则
    deliveryFeeRatioRules() {
      return [
        {
          required: true,
          message: this.$t('basicData.CannotBeEmpty'),
          trigger: ['blur', 'change']
        },
        {
          validator: this.validateDeliveryFeeRatio,
          trigger: ['blur', 'change']
        }
      ];
    },

    // 可用费用类型列表（过滤已选中的）
    enabledFeeList() {
      return this.feeList.filter(item => {
        return item.code && item.displayName;
      });
    },

    // 费用类型映射表（提高查找性能）
    feeTypeMap() {
      return this.feeList.reduce((map, item) => {
        if (item.code) {
          map[item.code] = item;
        }
        return map;
      }, {});
    }
  },

  watch: {
    tableData: {
      handler(newVal) {
        // 深度比较，避免不必要的更新
        if (this.isEqualArrays(newVal, this.formData.arr)) {
          return;
        }

        this.formData.arr = Array.isArray(newVal)
          ? cloneDeep(newVal)
          : [];

        this.updateSelectedFeeTypeCodes();
      },
      immediate: true,
      deep: true
    },

    // 使用debounce避免频繁触发父组件更新
    'formData.arr': {
      handler: debounce(function(newVal, oldVal) {
        if (this.isEqualArrays(newVal, oldVal)) {
          return;
        }
        this.$emit('change', newVal);
        this.updateSelectedFeeTypeCodes();
      }, 300),
      deep: true
    }
  },

  mounted() {
    this.updateSelectedFeeTypeCodes();
  },

  methods: {
    // 提交表单验证
    async submitForm() {
      try {
        await this.$refs.ruleForm.validate();
        return true;
      } catch (error) {
        console.error('表单验证失败:', error);
        return false;
      }
    },

    // 删除行
    handleDel(index) {
      this.formData.arr.splice(index, 1);
    },

    // 费用类型变更处理
    handleChange(feeTypeCode, index) {
      const feeType = this.feeTypeMap[feeTypeCode];
      if (feeType) {
        this.$set(this.formData.arr[index], 'specialRules', feeType.specialRules);
      }
    },

    // 费用比例输入处理
    handleDeliveryFeeRatioInput(value, index) {
      // 限制两位小数
      const regex = /^(?:([1-9]\d?(\.\d{1,2})?)|(0\.(0[1-9]|[1-9]\d?)))$/;
      console.log('value', regex.test(value), value);
      // 格式校验
      if (!regex.test(value)) {
        // this.$message.warning(this.$t('basicData.IncorrectFormatting'))
        return;
      }
      this.$set(this.formData.arr[index], 'deliveryFeeRatio', value);
    },

    // 获取费用类型显示标签
    getFeeTypeLabel(item) {
      return `${item.displayName || ''}(${item.code || ''})`;
    },

    // 判断费用类型是否禁用
    isFeeTypeDisabled(itemCode, currentRowCode, currentIndex) {
      // 当前行已选中的不禁用
      if (itemCode === currentRowCode) {
        return false;
      }

      // 检查其他行是否已选择该费用类型
      return this.formData.arr.some((row, index) =>
        index !== currentIndex && row.feeTypeCode === itemCode && row.feeTypeCode !== 'PT046'
      );
    },

    // 获取范围验证规则
    getRangeRules(compareValue, type) {
      return {
        required: true,
        compareValue: compareValue,
        validator: this.rangeValidator(type),
        trigger: ['blur', 'change']
      };
    },

    // 更新已选费用类型集合
    updateSelectedFeeTypeCodes() {
      const codes = this.formData.arr
        .map(item => item.feeTypeCode)
        .filter(code => code);

      this.selectedFeeTypeCodes = new Set(codes);
    },

    // 验证费用类型唯一性
    validateFeeTypeUniqueness(rule, value, callback) {
      if (!value) {
        return callback();
      }

      const selectedCount = this.formData.arr.filter(
        item => item.feeTypeCode === value && value !== 'PT046'
      ).length;

      if (selectedCount > 1) {
        return callback(new Error(this.$t('basicData.重复')));
      }

      callback();
    },

    // 验证配送费用比例
    validateDeliveryFeeRatio(rule, value, callback) {
      if (value === null || value === undefined || value === '') {
        return callback(new Error(this.$t('basicData.CannotBeEmpty')));
      }
      const regex = /^(?:([1-9]\d?(\.\d{1,2})?)|(0\.(0[1-9]|[1-9]\d?)))$/;
      console.log('value2', regex.test(value));
      // 格式校验
      if (!regex.test(value)) {
        return callback(new Error(this.$t('basicData.IncorrectFormatting'))); // '格式不正确'
      }

      callback();
    },

    // 数组深度比较
    isEqualArrays(arr1, arr2) {
      if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
        return arr1 === arr2;
      }

      if (arr1.length !== arr2.length) {
        return false;
      }

      // 简化比较逻辑，对于业务数据可接受
      for (let i = 0; i < arr1.length; i++) {
        const item1 = arr1[i];
        const item2 = arr2[i];

        if (JSON.stringify(item1) !== JSON.stringify(item2)) {
          return false;
        }
      }

      return true;
    },

    // 清空表单验证
    clearValidate() {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.clearValidate();
      }
    },

    // 重置表单
    resetForm() {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields();
      }
    }
  }
};
</script>
